import { useEffect, useRef } from 'react'
import Hammer from 'hammerjs'

type GestureHandlers = {
  onPan?: (event: HammerInput) => void
  onPanStart?: (event: HammerInput) => void
  onPanEnd?: (event: HammerInput) => void
  onPinch?: (event: HammerInput) => void
  onPinchStart?: (event: HammerInput) => void
  onPinchEnd?: (event: HammerInput) => void
  onRotate?: (event: HammerInput) => void
  onRotateStart?: (event: HammerInput) => void
  onRotateEnd?: (event: HammerInput) => void
  onTap?: (event: HammerInput) => void
  onDoubleTap?: (event: HammerInput) => void
}

export const useHammerGestures = (
  elementRef: React.RefObject<HTMLElement | SVGElement>,
  handlers: GestureHandlers,
  options?: Record<string, any>,
) => {
  const hammerRef = useRef<HammerManager | null>(null)

  useEffect(() => {
    console.log('useHammerGestures effect running', {
      hasOptions: !!options,
      hasElement: !!elementRef.current,
      elementType: elementRef.current?.constructor.name,
    })

    // Skip initialization if options are undefined (disabled state)
    if (!options) {
      console.log('Hammer.js: Skipping - no options provided')
      return
    }

    if (!elementRef.current) {
      console.log('Hammer.js: Skipping - no element ref')
      return
    }

    // Ensure element is actually in the DOM
    if (!elementRef.current.isConnected) {
      console.warn('Hammer.js: Element not connected to DOM')
      return
    }

    // Only work with HTMLElements, not SVGElements
    if (!(elementRef.current instanceof HTMLElement)) {
      console.warn('Hammer.js: Element must be HTMLElement, not SVGElement')
      return
    }

    try {
      // Create Hammer instance
      const hammer = new Hammer.Manager(elementRef.current, {
        ...options,
        touchAction: 'none',
        domEvents: true,
      })

      // Add only pinch and rotate recognizers for multi-touch gestures
      const pinchRecognizer = new Hammer.Pinch({ enable: true, threshold: 0.05 })
      const rotateRecognizer = new Hammer.Rotate({ enable: true, threshold: 5 })

      hammer.add([pinchRecognizer, rotateRecognizer])

      // Enable simultaneous recognition - CRITICAL for pinch + rotate
      pinchRecognizer.recognizeWith(rotateRecognizer)
      rotateRecognizer.recognizeWith(pinchRecognizer)

      // Set up event handlers
      if (handlers.onPan) hammer.on('pan', handlers.onPan)
      if (handlers.onPanStart) hammer.on('panstart', handlers.onPanStart)
      if (handlers.onPanEnd) hammer.on('panend', handlers.onPanEnd)
      if (handlers.onPinch) hammer.on('pinch', handlers.onPinch)
      if (handlers.onPinchStart) hammer.on('pinchstart', handlers.onPinchStart)
      if (handlers.onPinchEnd) hammer.on('pinchend', handlers.onPinchEnd)
      if (handlers.onRotate) hammer.on('rotate', handlers.onRotate)
      if (handlers.onRotateStart) hammer.on('rotatestart', handlers.onRotateStart)
      if (handlers.onRotateEnd) hammer.on('rotateend', handlers.onRotateEnd)
      if (handlers.onTap) hammer.on('tap', handlers.onTap)
      if (handlers.onDoubleTap) hammer.on('doubletap', handlers.onDoubleTap)

      hammerRef.current = hammer

      // Log successful initialization
      console.log('Hammer.js initialized successfully', {
        element: elementRef.current.tagName,
        options,
      })

      // Dispatch custom event to notify initialization
      window.dispatchEvent(
        new CustomEvent('hammer-initialized', {
          detail: {
            element: elementRef.current.tagName,
            hasGestures: true,
          },
        }),
      )
    } catch (error) {
      console.error('Failed to initialize Hammer.js:', error)
      window.dispatchEvent(
        new CustomEvent('hammer-error', {
          detail: { error: error?.toString() },
        }),
      )
    }

    // Cleanup
    return () => {
      if (hammerRef.current) {
        hammerRef.current.destroy()
        hammerRef.current = null
      }
    }
  }, [elementRef, options?.enable]) // Re-initialize when enable state changes

  return hammerRef
}
